import { PaperClip, SquareTwoStack } from '@medusajs/icons';
import { Button, Drawer, Input, Text, toast } from '@pcrm/fe/ui-kit';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Field } from '../../components/field';
import { ApiError } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { DOCUMENTS_QUERY_KEY, type DocumentListItem } from '../documents/documents.api';
import { subscriptionsQueryKey } from '../subscriptions/subscriptions.api';
import { sendToSigning } from './signing.api';
import { useCopySigningLink } from './use-copy-signing-link';

/**
 * „Trimite la semnat", pentru **un** document.
 *
 * ⚠️ **Aici era o listă cu bife**, iar sesiunea primea `documentIds`: un plic putea purta
 * oricâte documente ale aceluiași client. Plicul a dispărut cu totul (#115) — nu doar din
 * ecran, ci și din bază. Cazul obișnuit e acoperit: de la decizia din 2026-08-21
 * **contractul e un singur act**, cadru plus anexe, deci un rând înseamnă tot ce are
 * clientul de semnat. Ce s-a pierdut e trimiterea mai multor acte **distincte** pe un
 * singur link. Vezi docs/wiki/domains/signing.md
 *
 * Emailul nu e drumul principal: canalul real e WhatsApp, iar o instalare fără SMTP
 * trebuie să poată ajunge la semnătură ([#105](https://github.com/bplugaru/efx-pcrm/issues/105)).
 */
export function SendToSigningDrawer({
  document,
  onClose,
}: {
  /** `null` = sertarul e închis. Documentul e și titlul, deci nu se ține separat. */
  document: DocumentListItem | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const copyLink = useCopySigningLink();
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (document) {
      setEmail('');
    }
  }, [document]);

  const mutation = useMutation({
    mutationFn: (delivery: 'email' | 'link') => {
      if (!document) {
        throw new Error('Niciun document de trimis.');
      }

      return sendToSigning(document.id, {
        delivery,
        ...(email.trim() === '' ? {} : { email: email.trim() }),
      });
    },
    onSuccess: async (signing, delivery) => {
      if (delivery === 'link') {
        // Linkul se cere imediat: altfel operatorul închide sertarul și caută rândul în
        // tabel ca să-l scoată de acolo.
        copyLink.mutate(signing.documentId);
      } else {
        toast.success(`Linkul a plecat la ${signing.sentToEmail}.`);
      }

      await Promise.all([
        // Documentul a trecut în „trimis" și are de acum o sesiune: rândul lui trebuie
        // recitit, altfel meniul rămâne cu acțiunile stării dinainte.
        queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY }),
        // Abonamentele acoperite sunt de acum blocate la scriere: ecranul lor trebuie să
        // afle, altfel butoanele rămân active și eșuează la apăsare.
        // Doar când actul e al unui client: la semnat nu ajunge altfel (#133).
        ...(document && document.customerId !== null
          ? [
              queryClient.invalidateQueries({
                queryKey: subscriptionsQueryKey(document.customerId),
              }),
            ]
          : []),
      ]);
      onClose();
    },
    onError: (error: unknown) => {
      // Mesajul serverului spune și ce e de făcut („Invalidează sesiunea aceea întâi…").
      // Un text inventat aici ar fi al doilea adevăr despre aceeași regulă.
      toast.error(error instanceof ApiError ? error.message : 'Linkul nu a putut fi trimis.');
    },
  });

  return (
    <Drawer open={document !== null} onOpenChange={(next) => !next && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>
            {/*
              Fără număr cât actul e ciornă (#142): numărul îl primește chiar la trimiterea asta
              (#221). O retrimitere, după un link mort, îl are deja.
            */}
            {document
              ? `Trimite la semnat ${document.documentTypeName}${
                  document.documentNumber === null ? '' : ` nr. ${document.documentNumber}`
                }`
              : ''}
          </Drawer.Title>
        </Drawer.Header>

        <Drawer.Body className="flex flex-col gap-y-4 overflow-y-auto">
          {document ? (
            <Text size="small" className="text-ui-fg-subtle">
              {document.customerName} · {formatDate(document.documentDate)}
              {document.subscriptions.length === 0
                ? ''
                : ` · ${document.subscriptions.map((subscription) => subscription.name).join(', ')}`}
            </Text>
          ) : null}

          {/*
            ⚠️ **Spus înainte de apăsare, nu după** (#221): trimiterea consumă un număr din
            registru, iar din clipa aia actul nu se mai șterge — se anulează. Operatorul care
            vrea doar să vadă cum arată linkul trebuie să afle asta acum.
          */}
          {document !== null && document.documentNumber === null ? (
            <Text size="small" className="text-ui-fg-subtle">
              La trimitere, documentul primește următorul număr din registrul firmei, iar clientul
              îl vede cu numărul acesta. Dacă nu se mai semnează, documentul se anulează; nu se mai
              poate șterge.
            </Text>
          ) : null}

          <Field
            label="Trimite la altă adresă"
            id="signing-email"
            hint="Gol înseamnă adresa de pe fișa clientului."
          >
            <Input
              id="signing-email"
              type="email"
              value={email}
              placeholder="semnatar@exemplu.ro"
              onChange={(event) => setEmail(event.target.value)}
              disabled={mutation.isPending}
            />
          </Field>
        </Drawer.Body>

        <Drawer.Footer>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Renunță
          </Button>
          <Button
            variant="secondary"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate('email')}
          >
            <PaperClip />
            Trimite pe email
          </Button>
          <Button
            disabled={mutation.isPending}
            isLoading={mutation.isPending}
            onClick={() => mutation.mutate('link')}
          >
            <SquareTwoStack />
            Copiază linkul
          </Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  );
}
